import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import TabCode from "./TabCode.js";
import { t } from "i18next";
import {
  ArrowBackIcon,
  DriveFileRenameOutlineIcon,
} from "../../icons/index.tsx";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addModel,
  editModel,
  getModelDetail,
  getTrainModels,
} from "../../services/inspModelService.js";
import { CustomMuiButton } from "../../utils/styleUtils.js";
import "../../styles/addStyle.css";

export default function ModelFormPage() {
  const { id } = useParams();
  const [trainModelList, setTrainModelList] = useState([]);
  const [selectedTrainModel, setSelectedTrainModel] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    trainModel: null,
    options: "{}",
    pre_process: "{}",
    post_process: "{}",
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        // 학습 모델 목록 조회
        const trainModelsResponse = await getTrainModels({
          page: 1,
          pageSize: 300,
        });
        const filteredModels = trainModelsResponse.data.contents.filter(
          (model) => model.name !== "일괄학습"
        );
        setTrainModelList(filteredModels);

        // 수정 모드인 경우 상세 데이터 조회
        if (id) {
          const detailResponse = await getModelDetail(id);
          const detailData = {
            name: detailResponse.data.name,
            description: detailResponse.data.description,
            trainModel: detailResponse.data.trainModel,
            options: JSON.stringify(detailResponse.data.options),
            pre_process: JSON.stringify(detailResponse.data.pre_process),
            post_process: JSON.stringify(detailResponse.data.post_process),
          };
          setSelectedTrainModel(detailResponse.data.trainModel);
          setFormData(detailData);
          setInitialFormData(detailData);
        }
      } catch (error) {
        console.error("Data initialization failed:", error);
        // 에러 처리 로직 추가 (예: 에러 메시지 표시)
      }
    };

    initializeData();
  }, []);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getTrainModelList = async () => {
    try {
      const response = await getTrainModels();
      const filteredModels = response.data.contents.filter(
        (model) => model.name !== "일괄학습"
      );
      setTrainModelList(filteredModels);
    } catch (error) {
      console.error(error);
    }
  };

  const navigate = useNavigate();

  const handleSelectOption = (event, newValue) => {
    setSelectedTrainModel(newValue);
    updateFormData("trainModel", newValue);
    updateFormData("options", newValue.options);
    updateFormData("pre_process", newValue.pre_process);
    updateFormData("post_process", newValue.post_process);
  };

  const handleBackClick = () => {
    navigate("/home", { state: { tab: "insp_model" } });
  };

  const handleSubmit = async () => {
    try {
      const changedData = getChangedData();
      if (Object.keys(changedData).length === 0) {
        navigate("/home", { state: { tab: "insp_model" } });
        return;
      }

      const apiCall = id
        ? () => editModel(id, changedData)
        : () => addModel(changedData);

      await apiCall();
      navigate("/home", { state: { tab: "insp_model" } });
    } catch (error) {
      console.error("Submit failed:", error);
      // 에러 처리 로직 추가
    }
  };

  const handleTabCodeChange = (field, value) => {
    const fieldMapping = {
      options: "options",
      pre_process: "pre_process",
      post_process: "post_process",
    };
    if (typeof value === "string") {
      updateFormData(fieldMapping[field], value);
    } else {
      updateFormData(fieldMapping[field], JSON.stringify(value));
    }
  };

  const getChangedData = () => {
    if (!initialFormData) return formData;

    return Object.entries(formData).reduce((acc, [key, value]) => {
      if (value !== initialFormData[key]) {
        if (key === "trainModel") {
          acc.trainModel_id = value?.id;
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex" }}>
        <Button
          className="add-button"
          onClick={handleBackClick}
          sx={{ color: "var(--button-color)" }}
        >
          <ArrowBackIcon /> <span>{t("InspModel")}</span>
        </Button>
      </Box>
      <Box sx={{ height: "100%", width: "100%", marginTop: 3 }}>
        <Typography
          sx={{
            mb: 1,
            fontWeight: "700",
            fontSize: 24,
            display: "flex",
            alignItems: "center",
          }}
          fontFamily={"NotoSansKR-Variable"}
        >
          <DriveFileRenameOutlineIcon sx={{ marginRight: "5px" }} />
          {id ? t("EditModel") : t("CreateNewModel")}
        </Typography>
        <div className="input-container">
          <input
            type="text"
            id="input-field"
            placeholder="내용을 입력하세요"
            value={formData.name}
            onChange={(e) => updateFormData("name", e.target.value)}
            style={{
              border: "solid 1px gray",
            }}
          />
          <label htmlFor="input-field">Model Name</label>
        </div>
        <div className="input-container">
          <input
            type="text"
            id="input-field"
            placeholder="내용을 입력하세요"
            value={formData.description}
            onChange={(e) => updateFormData("description", e.target.value)}
            style={{
              border: "solid 1px gray",
            }}
          />
          <label htmlFor="input-field">Model Description</label>
        </div>
        <div className="input-container">
          <Autocomplete
            style={{ borderStyle: "none" }}
            options={trainModelList}
            freeSolo
            filterSelectedOptions
            getOptionLabel={(option) => option.name}
            onChange={handleSelectOption}
            value={selectedTrainModel}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("MsgSelectTrainModel")}
                sx={{ marginTop: "3px" }}
              />
            )}
            sx={{
              marginBottom: 3,
              "& .MuiChip-label": {
                color: "var(--text-color)",
              },
              "& .MuiAutocomplete-tag": {
                backgroundColor: "var(--chip-color)",
              },
              "& .MuiSvgIcon-root": {
                color: "var(--svg-color)",
              },
            }}
          />
          <label htmlFor="input-field">{t("TrainModel")}</label>
        </div>
        <TabCode
          firstData={formData.options}
          secondData={formData.pre_process}
          thirdData={formData.post_process}
          onCodeChange={handleTabCodeChange}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 3,
        }}
      >
        {id ? (
          <>
            <CustomMuiButton
              onClick={handleBackClick}
              variant="text"
              label={t("Cancel")}
            />
            <CustomMuiButton
              onClick={handleSubmit}
              variant="contained"
              label={t("Apply")}
            />
          </>
        ) : (
          <>
            <CustomMuiButton
              onClick={handleBackClick}
              variant="text"
              label={t("Cancel")}
            />
            <CustomMuiButton
              onClick={handleSubmit}
              variant="contained"
              label={t("Apply")}
            />
          </>
        )}
      </Box>
    </Box>
  );
}
